import { Injectable } from '@angular/core';
import {
	IBaseNotificationPayload,
	IEditResponse,
	IListNotificationsSet,
	INotificationPayload,
	INotificationsSet,
	Partial,
} from '@modules/notification-management/interfaces/notifications.interface';
import { NotificationsApiService } from '@modules/notification-management/services/notifications-api.service';
import { NotificationsStateService } from '@modules/notification-management/services/notifications-state.service';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, tap } from 'rxjs/operators';
import { WrapToasterService } from '@app/services/wrap-toaster-service';
import { ISlackChannelsResponse } from '@core/services/integrations/integrations.interface';
import { UserService } from '@app/services/user/user.service';

@Injectable({
	providedIn: 'root',
})
export class NotificationsService {
	constructor(
		private notificationsStateService: NotificationsStateService,
		private notificationsApiService: NotificationsApiService,
		private toasterService: WrapToasterService,
		private userService: UserService,
	) {}

	public setUpdating(value: boolean): void {
		this.notificationsStateService.setUpdating(value);
	}

	public setUpdatingModal(value: boolean): void {
		this.notificationsStateService.setUpdatingModal(value);
	}

	public fetchSlackChannels() {
		return this.notificationsApiService.getSlackChannels().pipe(
			tap((channels) => {
				this.notificationsStateService.setSlackChannels(channels);
			}),
		);
	}

	public getSlackChannels(): Observable<ISlackChannelsResponse> {
		return this.notificationsStateService.getSlackChannels();
	}

	public resetNotificationSet(): void {
		return this.notificationsStateService.resetNotificationSet();
	}

	public isUpdating$(): Observable<boolean> {
		return this.notificationsStateService.isUpdating$().pipe(distinctUntilChanged());
	}

	public isUpdatingModal$(): Observable<boolean> {
		return this.notificationsStateService.isUpdatingModal$().pipe(distinctUntilChanged());
	}

	public getNotificationSetsValue(): IListNotificationsSet[] {
		return this.notificationsStateService.getNotificationSetsValue();
	}

	public getNotificationSets(): Observable<IListNotificationsSet[]> {
		return this.notificationsStateService.getNotificationSets();
	}

	public get getCurrentOrganization(): string {
		return this.userService.organizationId;
	}

	public createNotificationEntity(notificationSet: INotificationPayload): Observable<IListNotificationsSet> {
		this.notificationsStateService.setUpdatingModal(true);
		return this.notificationsApiService.createNotificationEntity(notificationSet).pipe(
			tap((notificationSetResponse) => {
				this.notificationsStateService.pushNotificationSet(notificationSetResponse);
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
			finalize(() => {
				this.notificationsStateService.setUpdatingModal(false);
				this.notificationsApiService.dispatchRefreshNotificationList();
			}),
		);
	}

	public editNotificationEntity(notificationSet: Partial<INotificationPayload>): Observable<IEditResponse> {
		this.notificationsStateService.setUpdatingModal(true);
		return this.notificationsApiService.editNotificationEntity(notificationSet).pipe(
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
			finalize(() => {
				this.notificationsStateService.setUpdatingModal(false);
				this.notificationsApiService.dispatchRefreshNotificationList();
			}),
		);
	}

	public toggleNotificationEntityActive(notificationSet: Partial<INotificationPayload>): Observable<IEditResponse> {
		this.notificationsStateService.setUpdatingModal(true);
		return this.notificationsApiService.editNotificationEntity(notificationSet).pipe(
			tap(() => {
				this.notificationsStateService.toggleActivate(notificationSet.name);
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
			finalize(() => this.notificationsStateService.setUpdatingModal(false)),
		);
	}

	public deleteNotificationEntity(payload: Partial<INotificationsSet>): Observable<IEditResponse> {
		this.notificationsStateService.setUpdatingModal(true);
		return this.notificationsApiService.deleteNotificationEntity(payload).pipe(
			tap(() => {
				this.notificationsStateService.deleteNotificationSet(payload.name);
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
			finalize(() => this.notificationsStateService.setUpdatingModal(false)),
		);
	}

	public fetchNotificationEntities(payload: {}): Observable<IListNotificationsSet[]> {
		this.notificationsStateService.setUpdating(true);
		payload = { ...payload, org_id: +this.userService.organizationId };
		return this.notificationsApiService.fetchNotificationEntities(payload).pipe(
			tap((notificationSets) => {
				this.notificationsStateService.setNotificationSets(notificationSets);
				this.notificationsStateService.setUpdating(false);
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
		);
	}

	public getEntity(): Observable<INotificationsSet> {
		return this.notificationsStateService.getEntity();
	}

	public fetchEntity(payload: IBaseNotificationPayload): Observable<INotificationsSet> {
		this.notificationsStateService.setUpdatingModal(true);
		return this.notificationsApiService.getEntity(payload).pipe(
			tap((notificationSet) => {
				this.notificationsStateService.setNotificationSet(notificationSet);
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(null);
			}),
			finalize(() => this.notificationsStateService.setUpdatingModal(false)),
		);
	}
}
